import React from "react";
import { RouteComponentProps } from "react-router";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Row, Col, Button, message, Spin } from "antd";
import Card from "../components/Card";
import {
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import { AxiosResponse } from "axios";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import BaseForm from "../components/Form/BaseForm";
import { FormInstance } from "antd/lib/form";
import ModalCreator from "../components/ModalCreator";
import { SalesPerson } from "../helpers/shared-interfaces";
import {
  GET_SALES_PERSON,
  DELETE_SALES_PERSON,
  UPDATE_SALES_PERSON,
} from "../gql/salespeople";

interface State {
  isLoading: boolean;
  salesPerson: SalesPerson | null;
  hasChanged: false;
  showDialog: boolean;
  showApiDialog: false;
  showUpdateSalesPersonModal: boolean;
  isUpdatingSalesPerson: boolean;
}

interface RouteParams {
  id: string;
}

export default class ViewSalesPerson extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: React.RefObject<FormInstance>;
  constructor(props: RouteComponentProps<RouteParams>) {
    super(props);
    this.state = {
      isLoading: true,
      salesPerson: null,
      hasChanged: false,
      showDialog: false,
      showApiDialog: false,
      showUpdateSalesPersonModal: false,
      isUpdatingSalesPerson: false,
    };
    this.reference = React.createRef<FormInstance>();
    this.deleteSalesPerson = this.deleteSalesPerson.bind(this);
    this.getSalesPersonData = this.getSalesPersonData.bind(this);
  }

  toggleDeleteConfirmationModal = (): void => { this.setState((prevState) => ({ showDialog: !prevState.showDialog })); }

  redirectToAllSalesPeoplePage = (): void => {
    this.props.history.push("/sales-people");
  };

  getSalesPersonData(): void {
    const { id } = this.props.match.params;

    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_SALES_PERSON,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            this.setState({
              salesPerson: data.data.salesPerson,
            });
          } else {
            message.error("Failed to Get sales person data");
          }
        });
      },
    );
  }

  deleteSalesPerson(): void {
    const { id } = this.props.match.params;

    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_SALES_PERSON,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false, showDialog: false });
            if (!data.errors && data.data.deleteSalesPerson.success) {
              message.success("Sales person deleted successfuly");
              this.redirectToAllSalesPeoplePage();
            } else {
              message.error("Failed to delete sales person");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false, showDialog: false });
          });
      },
    );
  }

  componentDidMount(): void {
    this.getSalesPersonData();
  }

  toggleUpdateSalesPersonModal = (): void => {
    this.setState(
      (prevState) => ({
        showUpdateSalesPersonModal: !prevState.showUpdateSalesPersonModal,
      }),
      () => {
        if (this.state.showUpdateSalesPersonModal) {
        } else {
          this.reference.current?.resetFields();
        }
      },
    );
  };

  updateSalesPerson = async (): Promise<void> => {
    if (!this.reference.current) return;

    const { id } = this.props.match.params;

    try {
      const values = this.reference.current.getFieldsValue();

      this.setState({ isUpdatingSalesPerson: true });

      const response = await request({
        query: UPDATE_SALES_PERSON,
        variables: { ...values, id },
      });

      const { err, handled } = responseHandler(response, "updateSalesPerson");

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
      } else if (err) {
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Sales Person updated successfully");
        this.setState(
          {
            showUpdateSalesPersonModal: false,
          },
          this.getSalesPersonData,
        );
      }
    } catch (error) {
      handleValidationErrors(error);
    } finally {
      this.setState({ isUpdatingSalesPerson: false });
    }
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      showDialog,
      salesPerson,
      showUpdateSalesPersonModal,
      isUpdatingSalesPerson,
    } = this.state;
    return (
      <Spin spinning={isLoading}>
        <Card>
          <div>
            {showDialog &&
              ModalCreator({
                title: "Confirm Deletion Action",
                okText: "Yes",
                okType: "danger",
                showModal: showDialog,
                onOkay: this.deleteSalesPerson,
                toggleModal: this.toggleDeleteConfirmationModal,
                inscription: (
                  <div>Are you sure you want to delete this user?</div>
                ),
              })}
            <Row
              style={{
                marginBottom: "20px",
              }}
            >
              <Col
                style={{
                  textAlign: "left",
                }}
                span={8}
              >
                <div>Sales Person Name</div>
                <div>
                  <h1
                    style={{
                      fontSize: "16px",
                      marginBottom: "0px",
                      marginTop: "7px",
                    }}
                    data-cy="test-view-sales-person-username"
                  >
                    {salesPerson && salesPerson.name.toUpperCase()}
                  </h1>
                </div>
              </Col>
              <Col
                style={{
                  textAlign: "left",
                }}
                span={8}
              >
                <div
                  style={{
                    fontWeight: "bold",
                  }}
                >
                  Sales Person Code
                </div>
                <div>
                  <h1
                    style={{
                      fontSize: "15px",
                      marginBottom: "0px",
                      marginTop: "7px",
                    }}
                  >
                    {salesPerson && salesPerson.code}
                  </h1>
                </div>
              </Col>

              <Col
                style={{
                  textAlign: "right",
                }}
                span={8}
              >
                <EntitledSection entitlement={entitlements.Users.edit}>
                  <Button
                    icon={<EditOutlined />}
                    className="base-button"
                    size="large"
                    onClick={this.toggleUpdateSalesPersonModal}
                  >
                    Edit
                  </Button>
                </EntitledSection>
                <EntitledSection entitlement={entitlements.Users.delete}>
                  <Button
                    icon={<DeleteOutlined />}
                    size="large"
                    onClick={this.toggleDeleteConfirmationModal}
                    data-cy="test-user-delete-button"
                  >
                    Delete
                  </Button>
                </EntitledSection>
              </Col>
            </Row>
            {showUpdateSalesPersonModal &&
              ModalCreator({
                title: "Update Sales Person",
                okText: "Update",
                okType: "primary",
                showModal: showUpdateSalesPersonModal,
                onOkay: this.updateSalesPerson,
                toggleModal: this.toggleUpdateSalesPersonModal,
                inscription: (
                  <Spin spinning={isUpdatingSalesPerson}>
                    <BaseForm
                      layout="vertical"
                      reference={this.reference}
                      fieldsToHide={[]}
                      fields={[
                        {
                          name: "name",
                          label: "Name",
                          initialValue: salesPerson?.name,
                          inputType: "string",
                          placeholder: "Enter name",
                          required: false,
                          colConfig: { span: 22 },
                        },
                      ]}
                    />
                  </Spin>
                ),
              })}
          </div>
        </Card>
      </Spin>
    );
  }
}
